import { Heading } from '@assembly-js/design-system';
import clsx from 'clsx';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Channel as ChannelType } from 'stream-chat';
import {
  Channel,
  ChannelProps,
  SendButtonProps,
  SuggestionList,
  SuggestionListProps,
  UnreadMessagesSeparatorProps,
} from 'stream-chat-react';

import { ChatContentLoader } from 'src/features/channel/ChatContentLoader';
import { FileUploadButton } from 'src/features/channel/custom-message-input/ActionButtons';
import { CustomMessageInput } from 'src/features/channel/custom-message-input/CustomMessageInput';
import { CustomAutocompleteSuggestionItem } from 'src/features/channel/CustomAutoCompleteSuggestionItem';
import { CustomDateSeparator } from 'src/features/channel/CustomDateSeparator';
import { CustomSendButton } from 'src/features/channel/CustomSendButton';

interface ChannelProviderProps {
  MessageNotification: ChannelProps['MessageNotification'];
  EmptyStateIndicator?: ChannelProps['EmptyStateIndicator'];
  channel?: ChannelType;
  markReadOnMount?: boolean;
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
  } | null;
  children: ReactNode;
}

const CustomSuggestionList = (props: SuggestionListProps) => {
  return (
    <SuggestionList
      {...props}
      containerClassName={clsx(props.containerClassName, 'z-10 py-1!')}
    />
  );
};

const CustomUnreadMessagesSeparator = ({
  unreadCount,
}: UnreadMessagesSeparatorProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-fit py-0.5 bg-info-background">
      <Heading className="text-[#053299]" size="xs">
        {unreadCount
          ? t(($) => $.messages.unread_messages, { count: unreadCount })
          : t(($) => $.messages.unread_messages_default)}
      </Heading>
    </div>
  );
};

export const ChannelProvider = ({
  MessageNotification,
  EmptyStateIndicator,
  channel,
  markReadOnMount,
  aiAssistantStatus = null,
  children,
}: ChannelProviderProps) => {
  const customSendButton = useCallback(
    (props: SendButtonProps) => (
      <CustomSendButton {...props} aiAssistantStatus={aiAssistantStatus} />
    ),
    [aiAssistantStatus],
  );
  const disableComponentCallback = useCallback(() => null, []);

  return (
    <Channel
      channel={channel}
      markReadOnMount={markReadOnMount}
      SendButton={customSendButton}
      AttachmentSelectorInitiationButtonContents={FileUploadButton}
      DateSeparator={CustomDateSeparator}
      LoadingIndicator={ChatContentLoader}
      EmptyStateIndicator={EmptyStateIndicator}
      TypingIndicator={disableComponentCallback}
      activeUnreadHandler={disableComponentCallback} // `activeUnreadHandler` updates the document.title with unread count, but we don't want this because we have our own unread count in the header
      UnreadMessagesSeparator={CustomUnreadMessagesSeparator}
      UnreadMessagesNotification={disableComponentCallback} // Hides the unread message fab
      MessageNotification={MessageNotification}
      Input={CustomMessageInput}
      AutocompleteSuggestionList={CustomSuggestionList}
      AutocompleteSuggestionItem={CustomAutocompleteSuggestionItem}
    >
      {children}
    </Channel>
  );
};
